// Kanemori AutoCab: a driverless one-box robotaxi pod. Pale cyan livery with a checker band, glass canopy, light strips,
// roof taxi sign and a lidar puck. Numbers in hd px (see docs/CAR_ART.md).
import { K, dk, lt } from '../hd-kit';
import { registerCarModel, type CarModel, type V3 } from '../car-drawn';

const BODY: [number, number][] = [[-25, 3], [25, 3], [25, 8], [22.5, 9.6], [-22.5, 9.6], [-25, 8]]; // short nose, short tail, both bevelled
// edge 1 lower windscreen, 2 upper windscreen, 3 roof, 4 upper rear glass, 5 lower rear glass
const CABIN: [number, number][] = [[-23, 9.6], [19, 9.6], [15, 15.2], [9.5, 18.4], [-14, 18.4], [-21.5, 14.2]];
const HW = 11.5, HWC = 10, ROOF = 18.4, AX = 16;
const inArch = (u0: number, u1: number) => [AX, -AX].some(a => u1 > a - 5.8 && u0 < a + 5.8);

export const autocab: CarModel = {
  body: BODY, hw: HW, cabin: CABIN, hwc: HWC, axles: [AX, -AX], wheelR: 4.2,
  bodyColor: () => lt(K.cyan, .72), // fixed livery whatever the paint index
  bodyEdge(i, P) {
    const { fill, endRect } = P;
    const strip = (u: number, on: string, hi: string, off: string) => () => {
      fill(endRect(u, -HW, HW, 3, 4.6), K.metal); fill(endRect(u, -HW, HW, 3, 3.6), K.metalLo); // bumper
      fill(endRect(u, -9.6, 9.6, 5.1, 7.1), K.ink); // recessed light slot
      fill(endRect(u, -9, 9, 5.5, 6.7), P.lampOn ? on : off);
      if (P.lampOn) fill(endRect(u, -8, 8, 6, 6.4), hi);
    };
    if (i === 1) return strip(25, K.warm, K.warmHi, K.metalLo);
    if (i === 5) return strip(-25, K.red, K.pink, K.metalLo);
    return undefined;
  },
  bodySide(_s, v, P) {
    const { fill, sideRect, body } = P, seam = dk(body, .8);
    const W = P.wrecked ? dk(K.white, .55) : K.white;
    return () => {
      for (let u = -23; u < 23; u += 2) { // checker band between the wheel arches (sooty on a wreck, burnt away at the nose)
        if (inArch(u, u + 2) || (P.wrecked && u > 10)) continue;
        const a = ((u + 23) / 2) % 2 === 0;
        fill(sideRect(v, u, u + 1, 6.6, 7.8), a ? K.ink : W); fill(sideRect(v, u + 1, u + 2, 6.6, 7.8), a ? W : K.ink);
        fill(sideRect(v, u, u + 1, 5.4, 6.6), a ? W : K.ink); fill(sideRect(v, u + 1, u + 2, 5.4, 6.6), a ? K.ink : W);
      }
      fill(sideRect(v, -2.2, -1.4, 4.2, 9.4), seam); fill(sideRect(v, 5.3, 6.1, 4.2, 9.4), seam); // sliding door seams
      fill(sideRect(v, -20, 21, 8.6, 9.5), lt(body, .35)); // beltline highlight
    };
  },
  cabinEdge(i, P, { n, at }) {
    const { fill, body } = P;
    const glass = (a: number, b: number, inset: number): CabinFaceT => ({ color: P.tone(n), decal: () => {
      if (P.toward(n) < .72) return;
      const pane = [at(a, -HWC + inset), at(b, -HWC + inset), at(b, HWC - inset), at(a, HWC - inset)]; fill(pane, P.glassTone(n));
      if (!P.wrecked) fill([at(a, -HWC + inset), at(b, -HWC + inset), at(b, -HWC + inset + 1), at(a, -HWC + inset + 1)], K.glassHi);
      P.cracks(pane);
    } });
    if (i === 1) return glass(.1, .9, .9);
    if (i === 2 || i === 4) return glass(.1, .9, 1);
    if (i === 5) return glass(.12, .88, 1.4);
    if (i === 3) return { color: lt(body, .3), decal: () => { // roof: a dark panoramic glass canopy in a pale frame (a pale slab dominated the side views), taxi sign and lidar puck
      const z = ROOF + .01;
      const pane: V3[] = [[-12, -HWC + 2, z], [7.6, -HWC + 2, z], [7.6, HWC - 2, z], [-12, HWC - 2, z]];
      fill(pane, P.wrecked ? K.glassLo : K.glass);
      if (!P.wrecked) fill([[-12, -HWC + 2, z], [7.6, -HWC + 2, z], [7.6, -HWC + 3, z], [-12, -HWC + 3, z]], K.glassHi); else P.cracks([pane[0], pane[3], pane[2], pane[1]]);
      fill([[-14, -HWC + .5, z], [8, -HWC + .5, z], [8, -HWC + 1.4, z], [-14, -HWC + 1.4, z]], lt(body, .55));
      fill([[-14, HWC - 1.2, z], [9.5, HWC - 1.2, z], [9.5, HWC, z], [-14, HWC, z]], dk(body, .3)); // darker roof edge on the south
      const box = (u0: number, u1: number, v0: number, v1: number, z1: number, top: string, front: string, side: string) => {
        const zz = ROOF + .02;
        const faces: [V3, V3[], string][] = [
          [[1, 0, 0], [[u1, v0, zz], [u1, v1, zz], [u1, v1, z1], [u1, v0, z1]], front],
          [[-1, 0, 0], [[u0, v0, zz], [u0, v1, zz], [u0, v1, z1], [u0, v0, z1]], dk(front, .3)],
          [[0, 1, 0], [[u0, v1, zz], [u1, v1, zz], [u1, v1, z1], [u0, v1, z1]], side],
          [[0, -1, 0], [[u0, v0, zz], [u1, v0, zz], [u1, v0, z1], [u0, v0, z1]], lt(side, .25)],
        ];
        for (const [fn, pts, col] of faces) if (P.toward(fn) > 0) fill(pts, col);
        fill([[u0, v0, z1], [u1, v0, z1], [u1, v1, z1], [u0, v1, z1]], top);
      };
      if (P.wrecked) { // the sign is smashed down to a stub with a broken edge, the lidar puck torn off (an empty dark socket)
        box(-5, 2, -4.2, 4.2, ROOF + 1.2, K.ink, K.metalLo, dk(K.metalLo, .3));
        fill([[-4.4, -3.6, ROOF + 1.21], [-1, -1, ROOF + 1.21], [-3, 3.4, ROOF + 1.21]], K.warmLo); fill([[0, -3, ROOF + 1.21], [1.4, 2.6, ROOF + 1.21], [.4, 3.4, ROOF + 1.21]], K.metal);
        fill(P.disc(-11.2, 0, ROOF + .02, 1.8, 10).map(p => [p[0], p[2] - ROOF - .02, ROOF + .02] as V3), K.ink);
        fill([[6, -6, ROOF + .02], [9, -4, ROOF + .02], [7, 1, ROOF + .02], [4, -1, ROOF + .02]], K.woodLo); // scorch
        return;
      }
      box(-5, 2, -4.2, 4.2, ROOF + 3, K.warmHi, K.warm, K.warm); // taxi sign
      // small kanji-like mark on the sign's front face
      fill([[2.01, -2.5, ROOF + 1.3], [2.01, 2.5, ROOF + 1.3], [2.01, 2.5, ROOF + 1.8], [2.01, -2.5, ROOF + 1.8]], K.warmLo);
      fill([[2.01, -.3, ROOF + .6], [2.01, .3, ROOF + .6], [2.01, .3, ROOF + 2.5], [2.01, -.3, ROOF + 2.5]], K.warmLo);
      box(-13, -9.5, -1.7, 1.7, ROOF + 2.4, K.metalHi, K.metal, K.metalLo); // lidar puck
      fill([[-9.49, -1, ROOF + 1.2], [-9.49, 1, ROOF + 1.2], [-9.49, 1, ROOF + 1.6], [-9.49, -1, ROOF + 1.6]], K.cyan);
    } };
    return undefined;
  },
  cabinSide(s, v, P) {
    const n: V3 = [0, s, 0], { fill } = P;
    return () => {
      const gt = P.glassTone(n);
      const rear: V3[] = [[-21, v, 11.2], [-19.4, v, 15], [-15.4, v, 17.4], [-6, v, 17.4], [-6, v, 11.2]];
      const front: V3[] = [[-4, v, 11.2], [-4, v, 17.4], [8, v, 17.4], [13.5, v, 14.8], [17.5, v, 11.2]];
      fill(rear, gt); fill(front, gt); P.cracks(rear); P.cracks(front);
      if (!P.wrecked) { fill([[-21, v, 11.2], [-19.4, v, 15], [-18.6, v, 15.3], [-20.2, v, 11.2]], K.glassHi); fill([[-4, v, 11.2], [-4, v, 17.4], [-3.2, v, 17.4], [-3.2, v, 11.2]], K.glassHi); }
    };
  },
};
type CabinFaceT = ReturnType<NonNullable<CarModel['cabinEdge']>>;
registerCarModel('autocab', autocab);
